// ═══════════════════════════════════════════════════════════════════════════
//  MESIN AUDIO PEMUTAR KARAOKE
//
//  Tiga hal yang diminta, dan ketiganya BERBEDA cara kerjanya. Menganggapnya
//  satu masalah ("ubah suara lagunya") adalah cara tercepat mendapat tiga
//  tombol yang semuanya setengah jalan.
//
//    Tempo  — ditangani peramban sendiri (playbackRate + preservesPitch).
//    Nada   — butuh pergeseran pitch sungguhan; ditulis sebagai AudioWorklet.
//    Vokal  — dua cara yang SANGAT berbeda mutunya, tergantung berkasnya.
//
//  ── KENAPA TEMPO TIDAK BOLEH DIPAKAI UNTUK MENGUBAH NADA ──────────────────
//
//  Menaikkan playbackRate memang menaikkan nada — dan mempercepat lagunya.
//  Itu godaan yang besar karena satu baris kode. Tapi menaikkan nada 2
//  semitone berarti lagunya 12% lebih cepat, dan tamu yang minta nadanya naik
//  akan mendapat lagu yang berlari. Ia tidak akan bilang "temponya berubah",
//  ia akan bilang "kok jadi aneh".
// ═══════════════════════════════════════════════════════════════════════════

// ── PENGGESER NADA ─────────────────────────────────────────────────────────
//
// Garis tunda dengan dua titik baca yang saling menyilang (classic two-tap
// crossfading delay pitch shifter).
//
// Prinsipnya: untuk menaikkan nada sebesar r, titik baca harus berjalan r kali
// lebih cepat daripada titik tulis — jadi tundanya menyusut terus. Saat tunda
// itu habis dan harus melompat balik, akan ada sambungan yang terdengar
// sebagai "klik".
//
// Titik baca KEDUA dipasang setengah penyangga di belakang, dan keduanya
// disilangkan dengan bobot sin/cos. Bobot tiap titik tepat NOL pada saat ia
// melompat, jadi sambungannya jatuh di tempat yang tidak terdengar.
//
// Bobot sin & cos dipilih karena kuadratnya berjumlah satu — kerasnya suara
// tetap rata sepanjang penyilangan. Penyilangan linear akan turun ~3 dB di
// tengah, dan itu terdengar sebagai lagu yang "berdenyut" pelan.
const KODE_WORKLET = `
class PenggeserNada extends AudioWorkletProcessor {
  static get parameterDescriptors() {
    return [{ name: 'rasio', defaultValue: 1, minValue: 0.5, maxValue: 2,
              automationRate: 'k-rate' }]
  }
  constructor() {
    super()
    // 4096 sampel ≈ 85 ms pada 48 kHz.
    //
    // Lebih pendek: penyilangannya terlalu sering, terdengar bergetar.
    // Lebih panjang: tundanya terasa, dan tamu menyanyi di belakang musik —
    // yang jauh lebih merusak daripada sedikit artefak.
    this.N = 4096
    this.buf = [new Float32Array(this.N), new Float32Array(this.N)]
    this.tulis = 0
    this.tunda = 0
  }
  process(inputs, outputs, params) {
    const inp = inputs[0], out = outputs[0]
    if (!out || out.length === 0) return true
    // k-rate: satu nilai untuk seluruh blok. Dibaca dari params, BUKAN dari
    // properti this — AudioParam tidak pernah menjadi properti processor, dan
    // membacanya dari sana menghasilkan undefined yang diam-diam jadi 1.
    const r = params.rasio.length > 0 ? params.rasio[0] : 1
    const N = this.N, H = N / 2
    const n = out[0].length

    if (!inp || inp.length === 0) {
      for (const o of out) o.fill(0)
      return true
    }
    for (let i = 0; i < n; i++) {
      for (let ch = 0; ch < out.length; ch++) {
        const x = inp[ch] || inp[0]
        this.buf[ch][this.tulis] = x ? x[i] : 0
      }
      const fase = this.tunda / N
      const g1 = Math.sin(Math.PI * fase)
      const g2 = Math.cos(Math.PI * fase)

      let p1 = this.tulis - this.tunda
      while (p1 < 0) p1 += N
      let p2 = p1 + H
      while (p2 >= N) p2 -= N

      const i1 = Math.floor(p1), f1 = p1 - i1
      const i2 = Math.floor(p2), f2 = p2 - i2

      for (let ch = 0; ch < out.length; ch++) {
        const b = this.buf[ch]
        // Interpolasi linear: tundanya pecahan, dan membulatkannya
        // menghasilkan derau bernada tinggi yang paling terdengar justru
        // pada bagian lagu yang sepi.
        const a1 = b[i1] * (1 - f1) + b[(i1 + 1) % N] * f1
        const a2 = b[i2] * (1 - f2) + b[(i2 + 1) % N] * f2
        out[ch][i] = a1 * g1 + a2 * g2
      }
      this.tulis = (this.tulis + 1) % N
      this.tunda += (1 - r)
      while (this.tunda < 0) this.tunda += N
      while (this.tunda >= N) this.tunda -= N
    }
    return true
  }
}
registerProcessor('penggeser-nada', PenggeserNada)
`

/** Cara panduan vokal dimatikan — dan keduanya TIDAK sama mutunya. */
export type CaraVokal =
  | 'trek'    // ada berkas instrumental terpisah: bersih
  | 'kanal'   // pembatalan kanal tengah: ikut menipiskan bass & snare
  | 'tidak'   // mono / tidak mungkin dibatalkan

export type KeadaanAudio = {
  caraVokal: CaraVokal
  nadaSiap: boolean   // worklet berhasil dimuat?
  tempoSiap: boolean  // peramban bisa regang waktu tanpa mengubah nada?
}

export class MesinAudio {
  private ctx: AudioContext
  private sumber: MediaElementAudioSourceNode
  private geser: AudioWorkletNode | null = null
  private lewat: GainNode          // jalur tanpa geser nada
  private masukGeser: GainNode
  private kanalAsli: GainNode      // stereo apa adanya
  private kanalBatal: GainNode     // kanal tengah dibatalkan
  private keluar: GainNode
  private media: HTMLMediaElement
  private punyaTrekInstrumental: boolean

  keadaan: KeadaanAudio

  constructor(media: HTMLMediaElement, punyaTrekInstrumental: boolean) {
    this.media = media
    this.punyaTrekInstrumental = punyaTrekInstrumental
    const AC: typeof AudioContext =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    this.ctx = new AC()
    this.sumber = this.ctx.createMediaElementSource(media)

    this.lewat = this.ctx.createGain()
    this.masukGeser = this.ctx.createGain()
    this.keluar = this.ctx.createGain()

    // ── PEMBATALAN KANAL TENGAH ───────────────────────────────────────────
    //
    // L − R. Vokal biasanya dicampur tepat di tengah, jadi ia hilang. Tapi
    // bass dan snare juga di tengah, dan keduanya ikut hilang.
    //
    // Ini SELALU dibangun, tapi hanya dipakai kalau lagunya tidak punya trek
    // instrumental terpisah — dan layar diberi tahu mana yang sedang dipakai.
    const pisah = this.ctx.createChannelSplitter(2)
    const balik = this.ctx.createGain()
    balik.gain.value = -1
    const gabung = this.ctx.createGain()
    this.kanalAsli = this.ctx.createGain()
    this.kanalBatal = this.ctx.createGain()

    this.keluar.connect(pisah)
    pisah.connect(gabung, 0)
    pisah.connect(balik, 1)
    balik.connect(gabung)
    gabung.connect(this.kanalBatal)
    this.keluar.connect(this.kanalAsli)
    this.kanalAsli.connect(this.ctx.destination)
    this.kanalBatal.connect(this.ctx.destination)

    this.kanalAsli.gain.value = 1
    this.kanalBatal.gain.value = 0

    this.sumber.connect(this.lewat)
    this.lewat.connect(this.keluar)
    this.masukGeser.gain.value = 0

    this.keadaan = {
      caraVokal: punyaTrekInstrumental ? 'trek' : 'kanal',
      nadaSiap: false,
      // preservesPitch tidak ada di Safari lama. Kalau tidak ada, tombol tempo
      // TIDAK dipakai sama sekali — memakai playbackRate polos akan mengubah
      // nada juga, dan itu persis kesalahan yang seluruh berkas ini hindari.
      tempoSiap: 'preservesPitch' in (media as HTMLMediaElement & { preservesPitch?: boolean }),
    }
  }

  /** Memuat worklet. Gagal = tombol nada dimatikan, bukan pemutar mati. */
  async siapkan(): Promise<KeadaanAudio> {
    try {
      const blob = new Blob([KODE_WORKLET], { type: 'application/javascript' })
      const url = URL.createObjectURL(blob)
      await this.ctx.audioWorklet.addModule(url)
      URL.revokeObjectURL(url)

      this.geser = new AudioWorkletNode(this.ctx, 'penggeser-nada', {
        outputChannelCount: [2],
      })
      this.sumber.connect(this.masukGeser)
      this.masukGeser.connect(this.geser)
      this.geser.connect(this.keluar)
      this.keadaan.nadaSiap = true
    } catch {
      // AudioWorklet butuh konteks aman (https atau localhost). Di http biasa
      // ia gagal, dan gagalnya harus terbaca sebagai "tombol nada mati di
      // perangkat ini" — bukan sebagai lagu yang tidak mau bunyi.
      this.keadaan.nadaSiap = false
    }
    return this.keadaan
  }

  async bangunkan() {
    if (this.ctx.state === 'suspended') await this.ctx.resume()
  }

  /** Geser nada, dalam semitone. 0 = lewat jalur tanpa proses sama sekali. */
  setNada(semitone: number) {
    if (!this.geser) return
    const t = this.ctx.currentTime
    if (semitone === 0) {
      // Nada normal MELEWATI worklet sepenuhnya.
      //
      // Rasio 1.0 secara matematis tidak mengubah apa pun, tapi penyilangan
      // dua titik baca tetap berjalan dan tetap meninggalkan sedikit warna.
      // Tamu yang menekan +1 lalu kembali ke 0 akan mendengar lagunya belum
      // pulih, dan menyimpulkan tombolnya rusak.
      this.lewat.gain.setTargetAtTime(1, t, 0.02)
      this.masukGeser.gain.setTargetAtTime(0, t, 0.02)
      return
    }
    this.lewat.gain.setTargetAtTime(0, t, 0.02)
    this.masukGeser.gain.setTargetAtTime(1, t, 0.02)
    const p = this.geser.parameters.get('rasio')
    if (p) p.setValueAtTime(Math.pow(2, semitone / 12), t)
  }

  /** Tempo dalam persen (100 = asli). Nada TIDAK ikut berubah. */
  setTempo(persen: number) {
    const m = this.media as HTMLMediaElement & { preservesPitch?: boolean }
    if (!this.keadaan.tempoSiap) return
    m.preservesPitch = true
    m.playbackRate = Math.min(1.3, Math.max(0.7, persen / 100))
  }

  /**
   * Panduan vokal nyala/mati.
   *
   * `pindahTrek` dipanggil kalau lagunya punya berkas instrumental terpisah —
   * pemutar yang mengganti sumbernya, bukan mesin ini. Mesin ini hanya
   * mengurus jalur yang tidak punya pilihan itu.
   */
  setVokal(nyala: boolean, pindahTrek?: (pakaiInstrumental: boolean) => void) {
    if (this.punyaTrekInstrumental && pindahTrek) {
      this.keadaan.caraVokal = 'trek'
      pindahTrek(!nyala)
      return
    }
    this.keadaan.caraVokal = 'kanal'
    const t = this.ctx.currentTime
    this.kanalAsli.gain.setTargetAtTime(nyala ? 1 : 0, t, 0.05)
    this.kanalBatal.gain.setTargetAtTime(nyala ? 0 : 1, t, 0.05)
  }

  tutup() { this.ctx.close().catch(() => {}) }
}

// ═══════════════════════════════════════════════════════════════════════════
//  DETEKSI NADA DARI MIKROFON
//
//  Autokorelasi ternormalisasi. Dipilih daripada FFT karena yang dicari adalah
//  nada DASAR suara manusia (80–1000 Hz), dan suara manusia sering punya
//  harmonik yang lebih keras daripada dasarnya — FFT puncak-tertinggi akan
//  melaporkan oktaf yang salah pada suara yang justru paling bagus.
// ═══════════════════════════════════════════════════════════════════════════

export type Frame = { t: number; hz: number; keras: number }

// ── KENAPA TIPENYA DITURUNKAN DARI DOM, BUKAN DITULIS SENDIRI ──────────────
//
// TypeScript 5.7 membuat TypedArray menjadi generik atas buffer-nya:
// `Float32Array<ArrayBuffer>` berbeda dari `Float32Array<ArrayBufferLike>`,
// dan getFloatTimeDomainData hanya menerima yang pertama. `new Float32Array(n)`
// menghasilkan yang kedua.
//
// Akibatnya build gagal dengan kalimat yang menyebut SharedArrayBuffer —
// sesuatu yang tidak pernah dipakai di berkas ini sama sekali:
//
//   Type 'SharedArrayBuffer' is not assignable to type 'ArrayBuffer'
//
// package.json memaku typescript 5.4.5, tapi tidak ada package-lock.json dan
// tsconfig tidak menyetel "types" — jadi SETIAP paket @types di node_modules
// ikut termuat, termasuk yang menuntut lib versi lebih baru. Build di Mac dan
// build di dalam container tidak melihat tipe yang sama.
//
// Menulis `Float32Array<ArrayBuffer>` di sini akan memperbaiki container dan
// MERUSAK yang 5.4 ("Type 'Float32Array' is not generic"). Jadi tipenya
// diambil dari tanda tangan DOM-nya sendiri: apa pun yang diminta versi yang
// sedang dipakai, itu yang dipakai.
type BufNada = Parameters<AnalyserNode['getFloatTimeDomainData']>[0]

export class DengarMik {
  private ctx: AudioContext
  private analis: AnalyserNode
  private aliran: MediaStream
  private buf: BufNada

  private constructor(ctx: AudioContext, analis: AnalyserNode, aliran: MediaStream) {
    this.ctx = ctx
    this.analis = analis
    this.aliran = aliran
    this.buf = new Float32Array(analis.fftSize) as BufNada
  }

  static async buka(): Promise<DengarMik> {
    const aliran = await navigator.mediaDevices.getUserMedia({
      audio: {
        // Ketiganya WAJIB dimatikan.
        //
        // Peredam gema dan AGC dirancang untuk rapat online: keduanya menekan
        // suara yang stabil dan panjang — yaitu persis nada tahan yang sedang
        // kita nilai. Dibiarkan menyala, penyanyi yang paling stabil justru
        // dapat skor paling rendah.
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    })
    const AC: typeof AudioContext =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    const ctx = new AC()
    const src = ctx.createMediaStreamSource(aliran)
    const analis = ctx.createAnalyser()
    analis.fftSize = 2048
    src.connect(analis)
    return new DengarMik(ctx, analis, aliran)
  }

  /** Satu cuplikan. hz = 0 berarti tidak ada nada yang terbaca. */
  baca(): Frame {
    this.analis.getFloatTimeDomainData(this.buf)
    const n = this.buf.length
    let jml = 0
    for (let i = 0; i < n; i++) jml += this.buf[i] * this.buf[i]
    const keras = Math.sqrt(jml / n)

    // Di bawah ambang ini dianggap sunyi. Tanpa ambang, autokorelasi pada
    // derau ruangan tetap mengembalikan ANGKA — dan angka itu akan dinilai
    // seolah nyanyian, sehingga room yang kosong pun mendapat skor.
    if (keras < 0.012) return { t: performance.now() / 1000, hz: 0, keras }

    const laju = this.ctx.sampleRate
    const min = Math.floor(laju / 1000)  // 1000 Hz
    const maks = Math.floor(laju / 70)   // 70 Hz
    let terbaik = -1
    let nilaiTerbaik = 0
    let sebelum = 0
    let naik = false

    for (let lag = min; lag < maks && lag < n; lag++) {
      let c = 0, e1 = 0, e2 = 0
      for (let i = 0; i < n - lag; i++) {
        c += this.buf[i] * this.buf[i + lag]
        e1 += this.buf[i] * this.buf[i]
        e2 += this.buf[i + lag] * this.buf[i + lag]
      }
      const norm = c / (Math.sqrt(e1 * e2) + 1e-9)
      // Puncak PERTAMA yang cukup tinggi, bukan puncak tertinggi.
      //
      // Puncak tertinggi sering jatuh di kelipatan periode — hasilnya nada
      // yang dilaporkan satu oktaf terlalu rendah. Kesalahan satu oktaf pada
      // penilaian nada terlihat seperti penyanyi yang meleset jauh padahal
      // nadanya tepat.
      if (norm > sebelum) naik = true
      else if (naik && sebelum > 0.5) { terbaik = lag - 1; nilaiTerbaik = sebelum; break }
      else naik = false
      sebelum = norm
    }
    if (terbaik < 0 || nilaiTerbaik < 0.5) return { t: performance.now() / 1000, hz: 0, keras }
    return { t: performance.now() / 1000, hz: laju / terbaik, keras }
  }

  tutup() {
    this.aliran.getTracks().forEach(t => t.stop())
    this.ctx.close().catch(() => {})
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  PENILAIAN
//
//  ★ SATU HAL YANG PALING PENTING DI BERKAS INI ★
//
//  Ada DUA mode, dan namanya berbeda di layar karena yang diukurnya memang
//  berbeda:
//
//    'nada'  — ada melodi acuan. Dibandingkan nada per nada. Ini benar-benar
//              ketepatan nada.
//
//    'irama' — TIDAK ada melodi acuan. Yang dinilai: apakah tamu bersuara saat
//              liriknya jalan, dan seberapa stabil nadanya ditahan.
//
//  Mode 'irama' TIDAK BOLEH disebut "ketepatan nada" di layar mana pun.
//  Tanpa acuan, tidak ada yang tahu nada mana yang benar — dan angka yang
//  mengaku mengukurnya cuma tebakan yang dipercantik. Tamu akan
//  memercayainya: yang dapat 92 akan bangga, yang dapat 61 akan tersinggung,
//  dan keduanya tentang angka yang tidak mengukur apa pun.
// ═══════════════════════════════════════════════════════════════════════════

export type Nada = { t: number; d: number; m: number }
export type ModeSkor = 'nada' | 'irama'

const hzKeMidi = (hz: number) => 69 + 12 * Math.log2(hz / 440)

export class Penilai {
  mode: ModeSkor
  private melodi: Nada[]
  private offDetik: number
  private offSemi: number
  private geserNada: number

  private cocok = 0
  private dinilai = 0
  private stabil = 0
  private berpasangan = 0
  private bersuara = 0
  private berjendela = 0
  private hzTerakhir = 0
  private waktuTerakhir = -1

  constructor(opt: {
    mode: ModeSkor
    melodi?: Nada[] | null
    offsetDetik?: number
    offsetSemitone?: number
    geserNada: number
  }) {
    this.melodi = opt.melodi ?? []
    this.mode = this.melodi.length > 0 ? opt.mode : 'irama'
    this.offDetik = opt.offsetDetik ?? 0
    this.offSemi = opt.offsetSemitone ?? 0
    this.geserNada = opt.geserNada
  }

  /**
   * @param detikLagu posisi pemutaran, detik
   * @param liriknyaJalan apakah saat ini memang ada lirik yang harus dinyanyikan
   */
  suap(f: Frame, detikLagu: number, liriknyaJalan: boolean) {
    const dt = this.waktuTerakhir < 0 ? 0.04 : Math.min(0.2, f.t - this.waktuTerakhir)
    this.waktuTerakhir = f.t

    if (liriknyaJalan) this.berjendela += dt
    if (f.hz <= 0) { this.hzTerakhir = 0; return }
    this.bersuara += dt

    if (this.mode === 'nada') {
      const acuan = this.nadaPada(detikLagu)
      if (acuan !== null) {
        this.dinilai++
        const nyanyi = hzKeMidi(f.hz)
        // Selisih dihitung MODULO OKTAF.
        //
        // Perempuan yang menyanyikan lagu laki-laki akan menyanyi satu oktaf
        // di atas, dan itu BENAR — bukan meleset 12 semitone. Menilainya
        // sebagai meleset membuat setengah tamu tidak pernah bisa dapat skor
        // bagus, apa pun yang mereka lakukan.
        let d = (nyanyi - acuan) % 12
        if (d > 6) d -= 12
        if (d < -6) d += 12
        if (Math.abs(d) <= 0.5) this.cocok++
      }
    }

    if (this.hzTerakhir > 0) {
      this.berpasangan++
      const lompat = Math.abs(1200 * Math.log2(f.hz / this.hzTerakhir))
      // Di bawah 200 sen dianggap nada yang ditahan, bukan lompatan.
      // Suara yang stabil menghasilkan deret rapat; teriakan dan bicara
      // menghasilkan lompatan besar terus-menerus.
      if (lompat < 200) this.stabil++
    }
    this.hzTerakhir = f.hz
  }

  private nadaPada(detik: number): number | null {
    const t = detik - this.offDetik
    for (const n of this.melodi) {
      if (t >= n.t && t < n.t + n.d) {
        // geserNada ikut ditambahkan: kalau tamu menaikkan nada 2 semitone,
        // yang benar untuk DINYANYIKAN juga naik 2. Tanpa ini, setiap tamu
        // yang menggeser nada otomatis dinilai meleset sebanyak geserannya.
        return n.m + this.offSemi + this.geserNada
      }
      if (n.t > t) break
    }
    return null
  }

  hasil(): { skor: number; mode: ModeSkor; rincian: Record<string, number>; detikBersuara: number } {
    const cakupan = this.berjendela > 0 ? Math.min(1, this.bersuara / this.berjendela) : 0
    const stabil = this.berpasangan > 0 ? this.stabil / this.berpasangan : 0
    const tepat = this.dinilai > 0 ? this.cocok / this.dinilai : 0

    let skor: number
    if (this.mode === 'nada' && this.dinilai > 20) {
      skor = 100 * (0.65 * tepat + 0.20 * stabil + 0.15 * cakupan)
    } else {
      skor = 100 * (0.60 * cakupan + 0.40 * stabil)
    }

    // Mikrofon yang tidak masuk menghasilkan NOL, bukan skor rendah.
    //
    // Keduanya sama-sama angka kecil, tapi yang satu kerusakan alat dan yang
    // satu bukan — dan layar skor menampilkannya dengan kalimat yang berbeda.
    if (this.bersuara < 5) skor = 0

    return {
      skor: Math.round(Math.max(0, Math.min(100, skor))),
      mode: this.mode,
      rincian: {
        tepat: +tepat.toFixed(3),
        stabil: +stabil.toFixed(3),
        cakupan: +cakupan.toFixed(3),
        nada_dinilai: this.dinilai,
      },
      detikBersuara: +this.bersuara.toFixed(2),
    }
  }
}
